import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home, FileText, CreditCard, LineChart, UserCircle2,
  ChevronLeft, Ticket, Clock, Store, Tag, MapPin, Sun,
  CheckCircle2, Trash2, Edit3, Image as ImageIcon, X, Loader2,
  AlertTriangle,
} from 'lucide-react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const PRIMARY = '#2D5BFF';
const PRIMARY_DARK = '#1B3DBF';
const GRADIENT_HEADER = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const NAV_ITEMS = [
  { icon: Home, label: 'Home', key: 'home' },
  { icon: FileText, label: 'Receipts', key: 'receipts' },
  { icon: CreditCard, label: 'Cards', key: 'cards' },
  { icon: LineChart, label: 'Insights', key: 'insights' },
  { icon: UserCircle2, label: 'Profile', key: 'profile' },
];

const STATUS_STYLES = {
  expiring_soon: { bg: '#FFF7ED', text: '#EA580C', dot: '#F59E0B', label: 'Expiring soon' },
  active:        { bg: '#F0FDF4', text: '#16A34A', dot: '#22C55E', label: 'Active' },
  used:          { bg: '#F3F4F6', text: '#6B7280', dot: '#9CA3AF', label: 'Used' },
  expired:       { bg: '#FEF2F2', text: '#DC2626', dot: '#EF4444', label: 'Expired' },
};

const RETAILER_COLORS = {
  'Tesco':        { bg: '#1C3FAA', text: '#FFF' },
  "Sainsbury's":  { bg: '#F47721', text: '#FFF' },
  'B&Q':          { bg: '#1A1A1A', text: '#FFF' },
  'M&S':          { bg: '#1A1A1A', text: '#FFF' },
  'Boots':        { bg: '#005B3A', text: '#FFF' },
  'Nectar':       { bg: '#6B21A8', text: '#FFF' },
  'Asda':         { bg: '#78BE20', text: '#FFF' },
  'Waitrose':     { bg: '#5D8C3E', text: '#FFF' },
};

const getRetailerColor = (name) => {
  if (!name) return { bg: '#6B7280', text: '#FFF' };
  if (RETAILER_COLORS[name]) return RETAILER_COLORS[name];
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(RETAILER_COLORS)) {
    if (lower.includes(key.toLowerCase())) return val;
  }
  const hue = ((name.charCodeAt(0) || 65) * 137) % 360;
  return { bg: `hsl(${hue}, 55%, 45%)`, text: '#FFF' };
};

const getInitials = (name) => {
  if (!name) return '?';
  const w = name.trim().split(/\s+/);
  return w.length >= 2 ? (w[0][0] + w[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
};

const getDisplayStatus = (v) => {
  if (v.status === 'used') return 'used';
  if (v.status === 'expired') return 'expired';
  if (v.expiry_date) {
    const diff = Math.ceil((new Date(v.expiry_date + 'T23:59:59') - new Date()) / (1000 * 60 * 60 * 24));
    if (diff >= 0 && diff <= 7) return 'expiring_soon';
  }
  return 'active';
};

const getExpiryText = (v) => {
  if (v.status === 'used' && v.used_date) {
    return `Used ${new Date(v.used_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  if (!v.expiry_date) return null;
  const exp = new Date(v.expiry_date + 'T23:59:59');
  const diff = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return `Expired ${exp.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  if (diff === 1) return 'Expires tomorrow';
  if (diff <= 7) return `Expires in ${diff} days`;
  return `Valid until ${exp.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

const getValueDisplay = (v) => {
  if (v.value_label) return v.value_label;
  if (v.value != null) {
    const sym = v.currency === 'USD' ? '$' : v.currency === 'EUR' ? '€' : '£';
    return `${sym}${v.value % 1 === 0 ? v.value : v.value.toFixed(2)}`;
  }
  return null;
};

// ── Real scannable barcode using JsBarcode ────────────
const BarcodeDisplay = ({ value, height = 80, lineColor = '#000000', background = '#FFFFFF' }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current || !value) return;
    try {
      JsBarcode(svgRef.current, value, {
        format: 'CODE128',
        width: 1.8,
        height,
        displayValue: false,
        margin: 10,
        lineColor,
        background,
      });
    } catch {
      // value not encodable — number still shown by caller
    }
  }, [value, height, lineColor, background]);

  if (!value) return null;

  return <svg ref={svgRef} style={{ width: '100%', display: 'block' }} />;
};

// ── User avatar ───────────────────────────────────────
const UserAvatar = ({ name, avatarUrl, size = 40 }) => {
  if (avatarUrl) return <img src={avatarUrl} alt="" style={{ width: size, height: size, borderRadius: 999, objectFit: 'cover' }} />;
  return (
    <div className="flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, borderRadius: 999, backgroundColor: PRIMARY, color: '#FFF', fontSize: size * 0.38, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
      {name ? name.charAt(0).toUpperCase() : '?'}
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════════════════════

export default function VoucherDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);
  const [showUseMode, setShowUseMode] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [marking, setMarking] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const fetchVoucher = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vouchers/${id}`, { withCredentials: true });
      setVoucher(res.data.voucher);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      if (err.response?.status === 404) { toast.error('Voucher not found'); navigate(-1); return; }
      toast.error('Could not load voucher');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchVoucher(); }, [fetchVoucher]);

  const handleMarkUsed = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/vouchers/${id}/mark-used`, {}, { withCredentials: true });
      toast.success('Voucher marked as used!');
      fetchVoucher();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark as used');
    } finally {
      setMarking(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/vouchers/${id}`, { withCredentials: true });
      toast.success('Voucher deleted');
      navigate(-1);
    } catch (err) {
      toast.error('Could not delete voucher');
      setDeleting(false);
    }
  };

  const onNavClick = (key) => {
    const routes = { home: '/dashboard', receipts: '/receipts', cards: '/cards', insights: '/insights', profile: '/profile' };
    navigate(routes[key] || '/dashboard');
  };

  const userName = authUser?.first_name || authUser?.name?.split(' ')[0] || 'User';
  const lastName = authUser?.last_name || '';
  const userEmail = authUser?.email || '';
  const avatarUrl = authUser?.avatar_url || authUser?.picture || '';

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#D1D5DB' }} />
      </div>
    );
  }

  if (!voucher) return null;

  const displayStatus = getDisplayStatus(voucher);
  const statusStyle = STATUS_STYLES[displayStatus];
  const rc = getRetailerColor(voucher.retailer);
  const expiryText = getExpiryText(voucher);
  const valueDisplay = getValueDisplay(voucher);
  const isActive = voucher.status === 'active';
  const currencySymbol = voucher.currency === 'USD' ? '$' : voucher.currency === 'EUR' ? '€' : '£';

  // ── Fullscreen "Use Voucher" overlay ───────────────
  const UseVoucherOverlay = () => (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
      style={{ backgroundColor: '#FFFFFF' }}
      onClick={() => setShowUseMode(false)}
      data-testid="use-voucher-overlay"
    >
      {/* Close hint */}
      <button
        onClick={() => setShowUseMode(false)}
        className="absolute top-4 right-4 flex items-center justify-center"
        style={{ width: 44, height: 44, borderRadius: 999, backgroundColor: '#F3F4F6' }}
      >
        <X className="w-5 h-5" style={{ color: '#6B7280' }} />
      </button>

      <div className="flex flex-col items-center" style={{ maxWidth: 400, padding: '0 24px' }}>
        {/* Value */}
        {valueDisplay && (
          <p style={{ color: '#1B3DBF', fontSize: 48, fontWeight: 800, fontFamily: 'Manrope, sans-serif', marginBottom: 8 }}>
            {valueDisplay}
          </p>
        )}
        <p style={{ color: '#6B7280', fontSize: 16, fontFamily: 'DM Sans, sans-serif', textAlign: 'center', marginBottom: 32 }}>
          {voucher.description}
        </p>

        {/* Barcode */}
        {voucher.barcode_number && (
          <div
            className="flex flex-col items-center"
            style={{ backgroundColor: '#F9FAFB', borderRadius: 20, padding: '28px 32px' }}
          >
            <BarcodeDisplay value={voucher.barcode_number} height={90} />
            <p style={{ color: '#374151', fontSize: 18, fontFamily: 'monospace', letterSpacing: 4, marginTop: 16 }}>
              {voucher.barcode_number}
            </p>
          </div>
        )}

        {/* Brightness hint */}
        <div
          className="flex items-center gap-2 mt-8"
          style={{
            backgroundColor: '#FFFBEB',
            border: '1px solid #FDE68A',
            borderRadius: 999,
            padding: '10px 20px',
          }}
        >
          <Sun className="w-4 h-4" style={{ color: '#D97706' }} />
          <span style={{ color: '#92400E', fontSize: 13, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
            Screen brightness boosted for scanning
          </span>
        </div>
      </div>
    </div>
  );

  // ── Image preview modal ────────────────────────────
  const ImageModal = () => {
    if (!showImageModal || !voucher.image_url) return null;
    return (
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(15,23,42,0.7)' }}
        onClick={() => setShowImageModal(false)}
      >
        <div className="relative" style={{ maxWidth: 600, maxHeight: '80vh' }}>
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute -top-3 -right-3 flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          >
            <X className="w-4 h-4" style={{ color: '#374151' }} />
          </button>
          <img
            src={voucher.image_url}
            alt="Voucher"
            style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 16, objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    );
  };

  // ── Delete confirmation ────────────────────────────
  const DeleteConfirm = () => {
    if (!showDeleteConfirm) return null;
    return (
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(15,23,42,0.5)' }}
        onClick={() => setShowDeleteConfirm(false)}
      >
        <div
          className="bg-white"
          style={{ borderRadius: 18, padding: 28, maxWidth: 360, width: '100%', boxShadow: '0 24px 48px rgba(0,0,0,0.18)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-6 h-6" style={{ color: '#DC2626' }} />
            <h3 style={{ color: '#111827', fontSize: 18, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Delete voucher?</h3>
          </div>
          <p style={{ color: '#6B7280', fontSize: 14, fontFamily: 'DM Sans, sans-serif', marginBottom: 24 }}>
            This will permanently remove this voucher and its image. This can't be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1"
              style={{ backgroundColor: '#F3F4F6', color: '#374151', borderRadius: 12, padding: '12px 16px', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#DC2626', color: '#FFFFFF', borderRadius: 12, padding: '12px 16px', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Shared voucher detail content ──────────────────
  const VoucherContent = () => (
    <div>
      {/* Voucher card */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: 20,
          padding: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          marginBottom: 24,
        }}
      >
        {/* Retailer row */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: rc.bg, color: rc.text, fontSize: 14, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}
          >
            {getInitials(voucher.retailer)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold" style={{ color: '#111827', fontSize: 16, fontFamily: 'Manrope, sans-serif' }}>
                {voucher.retailer}
              </span>
              <span
                className="flex items-center gap-1 rounded-full"
                style={{ backgroundColor: statusStyle.bg, color: statusStyle.text, padding: '3px 10px', fontSize: 11, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: statusStyle.dot, display: 'inline-block' }} />
                {statusStyle.label}
              </span>
            </div>
            {voucher.linked_card && (
              <p style={{ color: '#6B7280', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                Just for you with your {voucher.linked_card} card
              </p>
            )}
          </div>
        </div>

        {/* Value + description */}
        <div className="flex items-baseline gap-3 mb-4">
          {valueDisplay && (
            <span style={{ color: '#1B3DBF', fontSize: 36, fontWeight: 800, fontFamily: 'Manrope, sans-serif' }}>
              {valueDisplay}
            </span>
          )}
          <span style={{ color: '#374151', fontSize: 16, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', lineHeight: 1.3 }}>
            {voucher.description}
          </span>
        </div>

        {/* Barcode section */}
        {voucher.barcode_number && (
          <>
            <div style={{ borderTop: '2px dashed #E5E7EB', margin: '16px 0' }} />
            <div className="flex flex-col items-center py-2">
              <BarcodeDisplay value={voucher.barcode_number} height={70} />
              <p style={{ color: '#374151', fontSize: 15, fontFamily: 'monospace', letterSpacing: 3, marginTop: 12 }}>
                {voucher.barcode_number}
              </p>
            </div>
          </>
        )}
      </div>

      {/* DETAILS section */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#1B3DBF', fontSize: 12, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16 }}>
          Details
        </h3>

        <div className="space-y-5">
          {/* What you get */}
          <div className="flex items-start gap-3">
            <Ticket className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
            <div>
              <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>What you get</p>
              <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>{voucher.description}</p>
            </div>
          </div>

          {/* Minimum spend */}
          {voucher.min_spend != null && (
            <div className="flex items-start gap-3">
              <Store className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
              <div>
                <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Minimum spend</p>
                <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>{currencySymbol}{voucher.min_spend.toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Expiry */}
          {expiryText && (
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
              <div>
                <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Expiry</p>
                <p style={{ color: displayStatus === 'expiring_soon' ? '#EA580C' : displayStatus === 'expired' ? '#DC2626' : '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
                  {expiryText}
                </p>
              </div>
            </div>
          )}

          {/* Where to use */}
          {voucher.where_to_use && (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
              <div>
                <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Where to use</p>
                <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
                  {voucher.where_to_use === 'in_store' ? 'In store' : voucher.where_to_use === 'online' ? 'Online' : 'In store & online'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Linked loyalty card */}
      {voucher.linked_card && (
        <div
          className="flex items-center gap-3 mb-5"
          style={{ backgroundColor: '#F8FAFC', borderRadius: 14, padding: '14px 16px' }}
        >
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#1B3DBF', color: '#FFF', fontSize: 11, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}
          >
            {voucher.linked_card.slice(0, 3)}
          </div>
          <div className="flex-1">
            <p style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>Loyalty card to scan</p>
            <p style={{ color: '#111827', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>{voucher.linked_card}</p>
          </div>
          <button
            onClick={() => navigate('/cards')}
            style={{ color: PRIMARY, fontSize: 13, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
          >
            In wallet
          </button>
        </div>
      )}

      {/* Conditions */}
      {voucher.conditions && (
        <div className="mb-5">
          <p style={{ color: '#6B7280', fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Conditions
          </p>
          <p style={{ color: '#374151', fontSize: 14, fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>
            {voucher.conditions}
          </p>
        </div>
      )}

      {/* Image link */}
      {voucher.image_url && (
        <button
          onClick={() => setShowImageModal(true)}
          className="flex items-center gap-2 mb-5"
          style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
        >
          <ImageIcon className="w-4 h-4" />
          View original photo
        </button>
      )}

      {/* Tip banner (only for active vouchers) */}
      {isActive && (
        <div
          className="flex items-start gap-3 mb-6"
          style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 14, padding: '14px 16px' }}
        >
          <span style={{ fontSize: 18 }}>💡</span>
          <p style={{ color: '#92400E', fontSize: 13, fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>
            <strong>Hang on to the paper coupon.</strong> Some shops still ask for the original at the till, so keep it safe until you've used this — don't throw it away just yet.
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-3">
        {isActive && voucher.barcode_number && (
          <button
            onClick={() => setShowUseMode(true)}
            className="w-full flex items-center justify-center gap-2"
            style={{ backgroundColor: PRIMARY, color: '#FFFFFF', borderRadius: 14, padding: 16, fontSize: 16, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', boxShadow: '0 4px 14px rgba(45,91,255,0.3)' }}
            data-testid="use-voucher-btn"
          >
            <Ticket className="w-5 h-5" />
            Use voucher
          </button>
        )}

        {isActive && (
          <button
            onClick={handleMarkUsed}
            disabled={marking}
            className="w-full flex items-center justify-center gap-2"
            style={{ backgroundColor: '#F3F4F6', color: '#374151', borderRadius: 14, padding: 16, fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}
            data-testid="mark-used-btn"
          >
            {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {marking ? 'Marking…' : 'Mark as used'}
          </button>
        )}

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full flex items-center justify-center gap-2"
          style={{ color: '#DC2626', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', padding: '12px 16px' }}
          data-testid="delete-voucher-btn"
        >
          <Trash2 className="w-4 h-4" />
          Delete voucher
        </button>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════
  //  MOBILE LAYOUT
  // ════════════════════════════════════════════════════

  if (isMobile) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F3F4F6' }} data-testid="voucher-detail-page">
        {showUseMode && <UseVoucherOverlay />}
        <ImageModal />
        <DeleteConfirm />

        {/* Gradient header */}
        <header
          style={{
            background: GRADIENT_HEADER,
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
            paddingBottom: 40,
            borderBottomLeftRadius: 28,
            borderBottomRightRadius: 28,
          }}
        >
          <div className="flex items-center justify-between px-5">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center"
              style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.15)' }}
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: '#FFFFFF' }} />
            </button>
            <h1 style={{ color: '#FFFFFF', fontSize: 18, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
              Voucher
            </h1>
            {voucher.image_url ? (
              <button
                onClick={() => setShowImageModal(true)}
                className="flex items-center justify-center"
                style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.15)' }}
              >
                <ImageIcon className="w-5 h-5" style={{ color: '#FFFFFF' }} />
              </button>
            ) : (
              <div style={{ width: 40 }} />
            )}
          </div>
        </header>

        {/* Content overlapping header */}
        <div style={{ padding: '0 20px', marginTop: -24, paddingBottom: 'calc(40px + env(safe-area-inset-bottom, 0px))' }}>
          <VoucherContent />
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  DESKTOP LAYOUT
  // ════════════════════════════════════════════════════

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F3F4F6' }} data-testid="voucher-detail-page">
      {showUseMode && <UseVoucherOverlay />}
      <ImageModal />
      <DeleteConfirm />

      {/* Sidebar */}
      <aside className="flex flex-col" style={{ width: 240, backgroundColor: '#F9FAFB', borderRight: '1px solid #F3F4F6' }}>
        <div className="flex items-center gap-2 px-5 pt-6 pb-8">
          <Ticket className="w-7 h-7" style={{ color: PRIMARY }} />
          <span style={{ color: '#111827', fontSize: 18, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>My Buddy</span>
        </div>
        <nav className="flex-1 px-3">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              onClick={() => onNavClick(item.key)}
              className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all"
              style={{ backgroundColor: 'transparent', color: '#6B7280', borderRadius: 10, fontSize: 14, fontWeight: 500, fontFamily: 'DM Sans, sans-serif' }}
              data-testid={`sidebar-nav-${item.key}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: '#F3F4F6' }}>
          <div className="flex items-center gap-3">
            <UserAvatar name={userName} avatarUrl={avatarUrl} size={40} />
            <div className="min-w-0">
              <p className="truncate" style={{ color: '#111827', fontSize: 14, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>{userName} {lastName}</p>
              {userEmail && <p className="truncate" style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>{userEmail}</p>}
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {/* Top bar */}
        <div className="flex items-center gap-3" style={{ padding: '20px 32px', borderBottom: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center hover:bg-gray-100 transition-colors"
            style={{ width: 36, height: 36, borderRadius: 10 }}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: '#6B7280' }} />
          </button>
          <h1 style={{ color: '#111827', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
            Voucher
          </h1>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 32px', maxWidth: 640 }}>
          <VoucherContent />
        </div>
      </main>
    </div>
  );
}